import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Minus, Plus, Trash2, ShoppingBag, ArrowRight } from "lucide-react";
import { useCart } from "@/context/CartContext";
import { formatCAD, resolveImg } from "@/lib/api";

export default function Cart() {
  const { items, updateQty, removeItem, subtotalCents, count } = useCart();
  const navigate = useNavigate();

  if (items.length === 0)
    return (
      <div data-testid="cart-empty" className="mx-auto max-w-2xl px-5 py-28 text-center">
        <img src="/brand/logo-mark.png" alt="" aria-hidden="true" className="mx-auto h-28 w-auto" />
        <h1 className="mt-6 font-display text-5xl font-semibold text-forest">Your cart is empty</h1>
        <p className="mt-3 text-muted-foreground">Fill it with the flavours of home.</p>
        <Link
          to="/shop"
          className="mt-8 inline-flex items-center gap-2 rounded-full bg-clay px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-[#A3161F]"
        >
          Browse the mart <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    );

  return (
    <div data-testid="cart-page" className="mx-auto max-w-6xl px-5 py-12 lg:px-10">
      <span className="label-eyebrow text-clay">Your basket</span>
      <h1 className="mt-2 font-display text-5xl font-semibold tracking-tight text-forest lg:text-6xl">Your cart</h1>
      <p className="mt-2 text-muted-foreground">{count} item{count !== 1 ? "s" : ""}</p>

      <div className="mt-10 grid gap-10 lg:grid-cols-3">
        <div className="space-y-4 lg:col-span-2">
          {items.map((item, i) => (
            <motion.div
              key={item.product_id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.05 }}
              data-testid={`cart-item-${item.product_id}`}
              className="flex gap-4 rounded-[1.5rem] border border-border/70 bg-white p-3 sm:p-4"
            >
              <Link to={`/product/${item.product_id}`} className="shrink-0">
                <img src={resolveImg(item.image_url)} alt={item.name} className="h-24 w-24 rounded-2xl bg-[#F6F1E6] object-cover mix-blend-multiply" />
              </Link>
              <div className="flex flex-1 flex-col">
                <div className="flex justify-between gap-3">
                  <div>
                    <Link to={`/product/${item.product_id}`} className="line-clamp-2 text-base font-semibold leading-snug text-ink hover:text-forest">
                      {item.name}
                    </Link>
                    <p className="text-xs text-muted-foreground">{item.unit}</p>
                  </div>
                  <button
                    data-testid={`remove-item-${item.product_id}`}
                    onClick={() => removeItem(item.product_id)}
                    className="text-muted-foreground transition-colors hover:text-destructive"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="mt-auto flex items-center justify-between pt-3">
                  <div className="flex items-center rounded-full border border-border">
                    <button
                      data-testid={`cart-dec-${item.product_id}`}
                      onClick={() => updateQty(item.product_id, item.quantity - 1)}
                      className="flex h-9 w-9 items-center justify-center rounded-full text-forest hover:bg-forest/5"
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <span className="w-8 text-center text-sm font-semibold">{item.quantity}</span>
                    <button
                      data-testid={`cart-inc-${item.product_id}`}
                      onClick={() => updateQty(item.product_id, item.quantity + 1)}
                      disabled={item.quantity >= item.stock}
                      className="flex h-9 w-9 items-center justify-center rounded-full text-forest hover:bg-forest/5 disabled:opacity-40"
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                  </div>
                  <span className="font-display text-lg font-semibold text-forest">{formatCAD(item.price_cents * item.quantity)}</span>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="lg:col-span-1">
          <div className="sticky top-36 overflow-hidden rounded-[1.75rem] border border-border/70 bg-white p-6">
            <h2 className="font-display text-3xl font-semibold text-forest">Summary</h2>
            <div className="mt-5 space-y-3 text-sm">
              <div className="flex justify-between text-muted-foreground">
                <span>Subtotal</span>
                <span className="font-semibold text-forest" data-testid="cart-subtotal">{formatCAD(subtotalCents)}</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Shipping</span>
                <span>Calculated at checkout</span>
              </div>
              <div className="flex justify-between text-muted-foreground">
                <span>Tax (GST)</span>
                <span>Calculated at checkout</span>
              </div>
            </div>
            <button
              data-testid="checkout-btn"
              onClick={() => navigate("/checkout")}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-clay py-4 text-sm font-bold text-white shadow-[0_14px_30px_-12px_rgba(200,32,43,0.7)] transition-colors hover:bg-[#A3161F]"
            >
              Checkout <ArrowRight className="h-4 w-4" />
            </button>
            <Link to="/shop" className="mt-3 block text-center text-sm text-muted-foreground hover:text-clay">
              Continue shopping
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
